(() => {
  const STORAGE_KEY = 'joyofdress:wishlist:v1';

  /** @returns {string[]} */
  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x) => typeof x === 'string' && x.trim().length).map((x) => x.trim());
    } catch {
      return [];
    }
  }

  /** @param {string[]} list */
  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(new Set(list))));
    } catch {
      // ignore
    }
  }

  /** @param {string} handle */
  function has(handle) {
    return readList().includes(handle);
  }

  /** @param {string} handle */
  function add(handle) {
    const list = readList();
    if (!list.includes(handle)) list.push(handle);
    writeList(list);
    window.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handles: list } }));
  }

  /** @param {string} handle */
  function remove(handle) {
    const list = readList().filter((h) => h !== handle);
    writeList(list);
    window.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handles: list } }));
  }

  /** @param {HTMLElement | Document} root */
  function syncButtons(root = document) {
    const list = readList();
    root.querySelectorAll?.('.js-wishlist-toggle[data-product-handle]').forEach((el) => {
      const handle = el.getAttribute('data-product-handle') || '';
      const pressed = list.includes(handle);
      el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      if (pressed) el.classList.add('is-active');
      else el.classList.remove('is-active');
    });
  }

  function syncHeaderCount() {
    const n = readList().length;
    const label = n ? `Wishlist, ${n} items` : 'Wishlist';
    document.querySelectorAll('.js-wishlist-header-count').forEach((el) => {
      el.textContent = String(n);
    });
    document.querySelectorAll('.header__cart-links--wishlist > a').forEach((link) => {
      link.setAttribute('aria-label', label);
    });
    document.querySelectorAll('.js-wishlist-sticky-link').forEach((link) => {
      link.setAttribute('aria-label', label);
    });
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      if (window.WAU?.Helpers?.formatMoney) {
        // WAU formatMoney signature differs across themes; keep simple fallback below.
      }
    } catch {
      // ignore
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed ${handle}`);
    return await res.json();
  }

  function renderWishlistPage(rootEl) {
    const handles = readList();
    const emptyHtml = `
      <div class="rte">
        <p>Your wishlist is empty.</p>
        <p><a href="/collections/all">Browse products</a></p>
      </div>
    `;

    if (!handles.length) {
      rootEl.innerHTML = emptyHtml;
      return;
    }

    rootEl.innerHTML = `<p class="rte">Loading…</p>`;

    Promise.allSettled(handles.map(fetchProduct)).then((results) => {
      const products = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);

      if (!products.length) {
        rootEl.innerHTML = emptyHtml;
        return;
      }

      const cards = products
        .map((p) => {
          const img = p.featured_image ? `<img src="${p.featured_image}" alt="${escapeHtml(p.title)}" loading="lazy">` : '';
          const price = money(p.price);
          return `
            <div class="wishlist-page__card">
              <a href="${p.url}">
                <div class="box-ratio" style="padding-bottom: 140%;">${img}</div>
                <div class="wishlist-page__meta">
                  <div class="h6 mb0">${escapeHtml(p.title)}</div>
                  <div class="product-loop__price mt2">${price}</div>
                </div>
              </a>
              <div class="wishlist-page__meta">
                <button type="button" class="button btn-outlined wishlist-page__remove js-wishlist-remove" data-handle="${escapeAttr(p.handle)}">Remove</button>
              </div>
            </div>
          `;
        })
        .join('');

      rootEl.innerHTML = `<div class="wishlist-page__grid">${cards}</div>`;
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  function onClick(e) {
    const toggle = e.target?.closest?.('.js-wishlist-toggle');
    if (toggle) {
      e.preventDefault();
      e.stopPropagation();

      const handle = toggle.getAttribute('data-product-handle') || '';
      if (!handle) return;
      const nowActive = !has(handle);
      if (nowActive) add(handle);
      else remove(handle);
      syncButtons(document);
      syncHeaderCount();
      return;
    }

    const removeBtn = e.target?.closest?.('.js-wishlist-remove');
    if (removeBtn) {
      e.preventDefault();
      const handle = removeBtn.getAttribute('data-handle') || '';
      if (!handle) return;
      remove(handle);
      const rootEl = document.querySelector('[data-wishlist-root]');
      if (rootEl) renderWishlistPage(rootEl);
      syncButtons(document);
    }
  }

  function init() {
    syncButtons(document);
    syncHeaderCount();
    document.addEventListener('click', onClick, { capture: true });
    window.addEventListener('wishlist:changed', () => {
      syncButtons(document);
      syncHeaderCount();
    });

    const rootEl = document.querySelector('[data-wishlist-root]');
    if (rootEl) renderWishlistPage(rootEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

